import React from "react";
import { Stack, StackProps, Text } from "@bookingcom/bui-react";
import { useUnifiedPriceTagController, type UnifiedPriceTagControllerProps } from "./useUnifiedPriceTag.controller";
import { UnifiedPriceTagPriceBreakdown } from "./UnifiedPriceTagPriceBreakdown";
import { UnifiedPriceTagPricePerAdultExplanation } from "./UnifiedPriceTagPricePerAdultExplanation";
import { UnifiedPriceTagExplanation } from "./UnifiedPriceTagExplanation";
import styles from "./UnifiedPriceTag.module.css";
import useRouteName from "../../../hooks/useRouteName";
import flights_lve_web_bf_price_strikethrough_alignment from "../../../utils/experiments/pricing/flights_lve_web_bf_price_strikethrough_alignment";

export type UnifiedPriceTagProps = UnifiedPriceTagControllerProps & Pick<StackProps, "attributes">;

export const UnifiedPriceTagDesktop = (props: UnifiedPriceTagProps) => {
  const { formattedPrice, formattedStrikethroughPrice, priceWrapperStackProps, priceStackProps, priceTextProps } =
    useUnifiedPriceTagController(props);
  const { attributes, priceLabel, strikethroughPriceProps, shouldShowPricePerAdult } = props;
  const routeName = useRouteName();
  const isInFlightDetails = routeName.includes("flightdetails");

  const inPriceStrikethroughExperimentDesktopBrandedFares =
    flights_lve_web_bf_price_strikethrough_alignment.useBfPriceStrikethroughVariant();

  const renderStrikethroughPrice = () => {
    if (!formattedStrikethroughPrice || shouldShowPricePerAdult) return null;
    return (
      <Text
        variant={strikethroughPriceProps?.variant || "body_2"}
        decoration="line-through"
        color="destructive"
        className={styles.strikedthroughPrice}
        attributes={{ "data-testid": "upt_price_strikethrough" }}
      >
        {formattedStrikethroughPrice}
      </Text>
    );
  };
  const renderTotalPrice = () => (
    <Text
      className={styles.price}
      {...priceTextProps}
      attributes={{ ...priceTextProps.attributes, "data-testid": "upt_price" }}
    >
      {formattedPrice}
    </Text>
  );
  const renderPriceLabel = () => priceLabel && <Text className={styles.noWrap}>{priceLabel}</Text>;

  const priceComponent = inPriceStrikethroughExperimentDesktopBrandedFares ? (
    <Stack {...priceStackProps} gap={0} attributes={attributes} className={styles.brandedFaresDesktopPrice}>
      <Stack direction="row" gap={1} className={styles.brandedFaresDesktopLabel}>
        {renderPriceLabel()}
        {renderStrikethroughPrice()}
      </Stack>
      {renderTotalPrice()}
    </Stack>
  ) : (
    <Stack {...priceStackProps}>
      {renderStrikethroughPrice()}
      <Stack direction="row" gap={1}>
        {renderPriceLabel()}
        {renderTotalPrice()}
      </Stack>
    </Stack>
  );

  return (
    <Stack direction="column" gap={0} attributes={{ "data-testid": "upt_container" }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={isInFlightDetails ? 0.5 : 1}
        {...priceWrapperStackProps}
        attributes={attributes}
      >
        {priceComponent}
        <UnifiedPriceTagPriceBreakdown {...props} />
      </Stack>
      <UnifiedPriceTagPricePerAdultExplanation {...props} />
      <UnifiedPriceTagExplanation {...props} />
    </Stack>
  );
};
